/**
 * Prelander Styles - Tutkimuskeskus 2026 Design System
 * Enhanced styling for the prelander survey experience
 */

const styles = theme => ({
    root: {
        display: 'flex'
    },
    formControl: {
        margin: theme.spacing(0)
    },
    group: {
        margin: `${theme.spacing(1)}px 0`
    },
    buttons: {
        marginTop: theme.spacing(1),
        marginBottom: 0,
        paddingBottom: '0!important'
    },
    button: {
        width: '100%',
        marginRight: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        color: '#fff'
    },
    questionPageFooterTextWrapper: {
        padding: theme.spacing(0, 0.25),
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        '& p:last-child': {
            marginBottom: 0
        },
        '& p': {
            fontSize: '0.95rem',
            lineHeight: 1.6,
            color: '#4a5d52'
        }
    },
    [theme.breakpoints.down(600)]: {
        buttons: {
            padding: '4px!important'
        },
        button: {
            padding: '4px 24px!important'
        }
    },
    rightIcon: {
        marginLeft: theme.spacing(1),
        marginTop: theme.spacing(0.75),
        marginBottom: theme.spacing(0.75),
        marginRight: -theme.spacing(1) * 0.5
    },
    radio: {
        minWidth: '110px'
    },
    selectButton: {
        marginTop: theme.spacing(1.7),
        marginRight: theme.spacing(2),
        minWidth: '112px'
    },
    logoImg: {
        filter: 'grayscale(100%)',
        opacity: 0.5
    },
    questionWrapper: {
        paddingBottom: theme.spacing(4),
        marginBottom: theme.spacing(-5) + 'px!important'
    },
    imageWrapper: {
        width: '100%',
        textAlign: 'center'
    },
    image: {
        maxWidth: '100%',
        height: 'auto'
    },
    dialog: {
        margin: '0 -24px!important'
    },
    questionContainer: {
        width: 'calc(100% + 16px)',
        margin: '-8px'
    },
    prelanderQuestion: {
        minHeight: 147
    },

    // === ENHANCED STYLES FOR 2026 DESIGN SYSTEM ===

    prelanderAnswerWrapper: {
        display: 'flex',
        justifyContent: 'center',
        margin: theme.spacing(0, -1),
        flexDirection: 'column',
        textAlign: 'center'
    },
    prelanderAnswer: {
        margin: theme.spacing(0.375, 0.5),
        padding: theme.spacing(0.5, 1.5),
        background: 'linear-gradient(135deg, #205a40 0%, #2d7a58 100%)',
        color: '#fff',
        borderRadius: 50,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 600,
        fontSize: '0.9rem',
        boxShadow: '0 2px 8px rgba(32, 90, 64, 0.25)',
        [theme.breakpoints.up(600)]: {
            padding: theme.spacing(0.75, 2),
            margin: theme.spacing(0.5, 0.5),
            fontSize: '1rem'
        }
    },
    prelanderAnswerBottomWrapper: {
        marginTop: theme.spacing(2),
        [theme.breakpoints.up(600)]: {
            marginTop: theme.spacing(3)
        }
    },

    // Question title for percentage results
    prelanderPercentageQuestion: {
        marginBottom: theme.spacing(1.5) + 'px!important',
        marginTop: theme.spacing(1),
        fontWeight: '700!important',
        fontSize: '1.1rem!important',
        textAlign: 'left!important',
        paddingLeft: theme.spacing(0.5) + 'px!important',
        fontFamily: "'Plus Jakarta Sans', sans-serif!important",
        color: '#1a2e23!important',
        lineHeight: '1.3!important',
        [theme.breakpoints.up(600)]: {
            marginBottom: theme.spacing(2) + 'px!important',
            fontSize: '1.15rem!important'
        }
    },

    // Percentage results container - magical glassmorphic card
    prelanderPercentageAnswersWrapper: {
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(20px)',
        boxShadow: `
            0 4px 24px rgba(32, 90, 64, 0.12),
            0 8px 32px rgba(32, 90, 64, 0.08),
            0 0 0 1px rgba(255, 255, 255, 0.5) inset,
            0 0 40px rgba(32, 90, 64, 0.06)
        `,
        borderRadius: '20px!important',
        border: '1px solid rgba(32, 90, 64, 0.1)',
        padding: theme.spacing(1.5, 1),
        overflow: 'hidden',
        position: 'relative',
        [theme.breakpoints.up(600)]: {
            padding: theme.spacing(2, 1.5),
            borderRadius: '24px!important'
        },
        // Subtle gradient border effect
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: 'inherit',
            padding: 1,
            background: 'linear-gradient(135deg, rgba(32, 90, 64, 0.2) 0%, rgba(45, 122, 88, 0.1) 50%, rgba(32, 90, 64, 0.2) 100%)',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none'
        }
    },

    // Individual percentage bar row
    prelanderPercentageAnswerWrapper: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: theme.spacing(1, 0.5),
        padding: theme.spacing(0.75, 0),
        background: 'transparent',
        overflow: 'hidden',
        minHeight: 44,
        borderRadius: 12,
        transition: 'transform 0.2s ease',
        [theme.breakpoints.up(600)]: {
            margin: theme.spacing(1.25, 0.75),
            minHeight: 48
        }
    },
    prelanderPercentageAnswerWrapperChecked: {
        // Checked state handled by background element
    },

    // Background bar for checked state
    prelanderPercentageAnswerCheckedBackground: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, rgba(32, 90, 64, 0.12) 0%, rgba(32, 90, 64, 0.06) 100%)',
        borderRadius: 12,
        zIndex: 0
    },

    // Option label text
    prelanderPercentageLabel: {
        zIndex: 2,
        lineHeight: 1.3,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        margin: theme.spacing(0.5, 0.5),
        padding: theme.spacing(0.5, 1),
        fontWeight: 500,
        fontSize: '0.95rem',
        textAlign: 'left',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        color: '#4a5d52',
        flex: 1,
        minWidth: 0,
        [theme.breakpoints.down(600)]: {
            fontSize: '0.9rem'
        }
    },
    prelanderPercentageLabelChecked: {
        color: '#ffffff',
        fontWeight: 700,
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3), 0 0 20px rgba(32, 90, 64, 0.4)'
    },

    // "Sinä" badge for selected option
    yourAnswerBadge: {
        // display: 'inline-flex',
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255, 255, 255, 0.95)',
        color: '#205a40',
        fontSize: '0.65rem',
        fontWeight: 700,
        padding: '4px 12px',
        borderRadius: 50,
        letterSpacing: '0.3px',
        textTransform: 'uppercase',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25), 0 0 12px rgba(255, 255, 255, 0.5)',
        marginLeft: 6,
        textShadow: 'none'
    },

    // Percentage value
    prelanderPercentageValue: {
        zIndex: 2,
        minWidth: 50,
        padding: '4px 10px',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        margin: theme.spacing(0.5, 0.75),
        fontWeight: 700,
        fontSize: '0.95rem',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        color: '#1a2e23',
        [theme.breakpoints.down(600)]: {
            fontSize: '0.9rem',
            minWidth: 45
        }
    },
    prelanderPercentageValueChecked: {
        color: '#205a40',
        fontWeight: 800,
        textShadow: '0 1px 2px rgba(255, 255, 255, 0.5), 0 2px 4px rgba(255, 255, 255, 0.3), 0 0 20px rgba(32, 90, 64, 0.4)'
    },

    // Progress bar
    prelanderPercentageBar: {
        position: 'absolute',
        left: 0,
        top: 0,
        height: '100%',
        background: 'rgba(32, 90, 64, 0.08)',
        borderRadius: 12,
        transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 0
    },
    prelanderPercentageBarChecked: {
        background: 'linear-gradient(90deg, #205a40 0%, #2d7a58 100%)'
    },

    // Next button wrapper
    nextButtonWrapper: {
        width: '100%',
        marginTop: theme.spacing(4),
        [theme.breakpoints.up(600)]: {
            marginTop: theme.spacing(5)
        },
        marginBottom: -24,
        paddingBottom: 32,
        '& button': {
            background: 'linear-gradient(135deg, #205a40 0%, #2d7a58 100%)!important',
            boxShadow: '0 4px 16px rgba(32, 90, 64, 0.3)!important',
            borderRadius: '14px!important',
            padding: '14px 32px!important',
            fontFamily: "'Plus Jakarta Sans', sans-serif!important",
            fontWeight: '600!important',
            fontSize: '1.05rem!important',
            textTransform: 'none!important',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease!important',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 24px rgba(32, 90, 64, 0.4)!important',
                background: 'linear-gradient(135deg, #1a4d36 0%, #257050 100%)!important'
            }
        }
    }
})

export { styles }
